/**
 * @file JS - Class - VectorLayerStyle.js
 * @author Adam Lemire
 * @since 2019/11/12
 * @version 1.0
 * @summary Dernières modifications : 2022/04/26
 * @namespace vectorLayerStyle
 */

import { ClassificationType, ColorGradient, ColorHexList, GradationType, Luminosity, StyleTypes, FeatureType } from '../../helpers/Constants.js';
import Geostats from 'geostats';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style.js';
import '../../helpers/typedef.js';
import { dictionary } from '../../helpers/languageDictionary.js';

const STROKE_WIDTH = 2;
const RADIUS = 6;

/**
 * @description Classe VectorLayerStyle qui contient les variables de style de VectorLayer
 * @example const vectorLayerStyle = new VectorLayerStyle();
 */
export default class VectorLayerStyle {
  constructor ({ styleFromJSON = false }) {
    if (styleFromJSON) {
      this._styleType = styleFromJSON.styleType;
      this._symbolShape = styleFromJSON.symbolShape;
      this._groupValueColumn = styleFromJSON.groupValueColumn;
      this._symbolStyles = convertirSymbolStylesDepuisJSON(styleFromJSON.symbolStyles);
      this._singleStyle = styleFromJSON.singleStyle;
      this._styleAutres = styleFromJSON.styleAutres;
      this._styleLabel = styleFromJSON.styleLabel;
      this._gradationType = styleFromJSON.gradationType;
      this._colorGradient = styleFromJSON.colorGradient;
      this._colorTheme = styleFromJSON.colorTheme;
      this._classificationJSON = styleFromJSON.classification;
      this._classification = {};
    } else {
      /** @type { StyleTypes } */
      this._styleType = StyleTypes.SINGLE;
      /** @type { string } */
      this._symbolShape = 'circle';
      /** @type { string } */
      this._groupValueColumn = '';
      /** @type { Map < number, GroupStyle > } */
      this._symbolStyles = new Map();
      /** @type { Luminosity } */
      this._colorTheme = Luminosity.BRIGHT;
      /** @type { ColorGradient } */
      this._colorGradient = ColorGradient.RED;
      /** @type { Classification } */
      this._classification = { type: undefined, groups: undefined, precision: undefined, nbGroupes: 0 };
      /** @type { GradationType } */
      this._gradationType = GradationType.COLOR;

      const colorPosition = Math.floor((Math.random() * 9));
      const fillColor = ColorHexList.BRIGHT[colorPosition];
      const strokeColor = ColorHexList.DARK[colorPosition];

      this._symbolStyles.set(0, {
        id: 0,
        fillColor: fillColor,
        strokeColor: strokeColor,
        strokeWidth: STROKE_WIDTH,
        radius: RADIUS,
        zIndex: 2,
        groupLabel: '',
        groupValueLabel: '',
        groupValue: ''
      });

      this._singleStyle = {
        fillColor: fillColor,
        strokeColor: strokeColor,
        strokeWidth: STROKE_WIDTH,
        radius: RADIUS,
        zIndex: 2,
        groupLabel: '',
        groupValue: ''
      };

      this._styleAutres = {
        visible: false,
        fillColor: '#B4B4B4',
        strokeColor: '#787878',
        strokeWidth: 1,
        radius: RADIUS,
        zIndex: 1
      };

      this._styleLabel = {
        backgroundColor: 'rgba(255,255,255,0)',
        backgroundColorNumbers: [255, 255, 255],
        backgroundOpacity: 0,
        fontColor: '#000',
        fontSize: 12,
        haloWidth: 0,
        haloColor: 'rgba(255,255,255,0)',
        haloOpacity: 0,
        haloColorNumbers: [255, 255, 255],
        padding: [2, 2, 2, 2],
        offsetX: 0,
        offsetY: 0
      };
    }
  }

  get classification () { return this._classification; }

  set classification (classification) { this._classification = classification; }

  get styleType () { return this._styleType; }

  set styleType (styleType) { this._styleType = styleType; }

  get symbolShape () { return this._symbolShape; }

  set symbolShape (symbolShape) { this._symbolShape = symbolShape; }

  get gradationType () { return this._gradationType; }

  set gradationType (gradationType) { this._gradationType = gradationType; }

  get groupValueColumn () { return this._groupValueColumn; }

  set groupValueColumn (groupValueColumn) { this._groupValueColumn = groupValueColumn; }

  get symbolStyles () { return this._symbolStyles; }

  set symbolStyles (symbolStyles) { this._symbolStyles = symbolStyles; }

  get singleStyle () { return this._singleStyle; }

  set singleStyle (singleStyle) { this._singleStyle = singleStyle; }

  get colorGradient () { return this._colorGradient; }

  set colorGradient (colorGradient) { this._colorGradient = colorGradient; }

  get colorTheme () { return this._colorTheme; }

  set colorTheme (colorTheme) { this._colorTheme = colorTheme; }

  get styleAutres () { return this._styleAutres; }

  set styleAutres (styleAutres) { this._styleAutres = styleAutres; }

  get styleLabel () { return this._styleLabel; }

  set styleLabel (styleLabel) { this._styleLabel = styleLabel; }

  /**
   * @param { number | string } index
   * @returns { GroupStyle }
   */
  getSymbolStyle (index = 0) {
    if (this.styleType === StyleTypes.GRADUATED) {
      const symbolStyle =
        this.classification.type === ClassificationType.ATTRIBUTE
          ? this._symbolStyles.get(parseInt(index)) || this._symbolStyles.get(index)
          : this._symbolStyles.get(this.classification.groups.getClass(index));
      // console.log(this._symbolStyles);
      return symbolStyle;
    } else {
      const style = this._symbolStyles.get(parseInt(index)) || this._symbolStyles.get(index);
      return style;
    }
  }

  /**
   * @static
   * @param { number[] | string[] } valeurAttributs
   * @returns { Geostats }
   */
  static obtenirClassificationParValeurAttributs (valeurAttributs) {
    const groupsStat = new Geostats(valeurAttributs);
    groupsStat.getUniqueValues();
    groupsStat.setRanges();
    console.log(groupsStat);
    return groupsStat;
  }

  /**
   * @static
   * @param { number[] | string[] } valeurAttributs
   * @param { number } precision
   * @param { number } nbGroupes
   * @returns { Geostats }
   */
  static obtenirClassificationParIntervalEgaux (valeurAttributs, precision, nbGroupes) {
    const groupsStat = new Geostats(valeurAttributs);
    groupsStat.getEqInterval(nbGroupes);
    const groupsStatPrecis = modifierClassificationSelonPrecision(groupsStat, precision);

    return groupsStatPrecis;
  }

  /**
   * @static
   * @param { number[] | string[] } valeurAttributs
   * @param { number } precision
   * @param { number } nbGroupes
   * @returns { Geostats }
   */
  static obtenirClassificationParProgressionGeometrique (valeurAttributs, precision, nbGroupes) {
    const groupsStat = new Geostats(valeurAttributs);
    groupsStat.getGeometricProgression(nbGroupes);
    const groupsStatPrecis = modifierClassificationSelonPrecision(groupsStat, precision);

    return groupsStatPrecis;
  }

  /**
   * @static
   * @param { number[] | string[] } valeurAttributs
   * @param { number } precision
   * @param { number } nbGroupes
   * @returns { Geostats }
   */
  static obtenirClassificationParJenks (valeurAttributs, precision, nbGroupes) {
    const groupsStat = new Geostats(valeurAttributs);
    groupsStat.getJenks(nbGroupes);
    const groupsStatPrecis = modifierClassificationSelonPrecision(groupsStat, precision);

    return groupsStatPrecis;
  }

  /**
   * @param { number[] | string[] } valeurAttributs
   * @returns { Geostats }
   */
  static obtenirClassificationParEcartType (valeurAttributs) {
    const lang = window.localStorage.getItem('lang');
    const groupsStat = new Geostats(valeurAttributs);
    const ecartType = groupsStat.stddev();
    const moyenne = groupsStat.mean();
    const bounds = [groupsStat.min(), moyenne - 2 * ecartType, moyenne - 0.5 * ecartType, moyenne + 0.5 * ecartType, moyenne + 2 * ecartType, groupsStat.max()];
    const newRanges = lang === 'en'
      ? dictionary.classification.stdDeviationLegendArray.en
      : dictionary.classification.stdDeviationLegendArray.fr;

    groupsStat.bounds = bounds;
    groupsStat.ranges = newRanges;

    return groupsStat;
  }

  // non-fonctionnel, classement ne marche pas
  /* obtenirClassificationParQuantile (valeurAttributs, precision) {
    let groupsStat = new Geostats(valeurAttributs);
    const groupQuantity = parseInt($(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_SELECTOR).val());
    const quantiteValeursParClasse = Math.round(valeurAttributs.length / groupQuantity);
    const valeurUniques = valeurAttributs.filter((v, i, a) => a.indexOf(v) === i);

    console.log('valeurUniques: %o', valeurUniques);
    console.log('valeurUnique.length = %o', valeurUniques.length);
    console.log('groupQuantity = %o', groupQuantity);
    if (valeurUniques.length === groupQuantity) {
      valeurUniques.unshift(valeurUniques[0]); // dupliquer le 1er élément, nécessaire car bounds.length doit avoir 1 de + que ranges.length
      groupsStat.bounds = valeurUniques;
    } else {
      const limites = [valeurUniques[0]];

      for (let i = 0; i < groupQuantity; i++) {
        console.log('i: %o', i);
        const valeurClasseDebut = limites[i];
        const indexValeurDebut = valeurUniques.indexOf(valeurClasseDebut);
        let valeurClasseFinPosition;

        if (i < groupQuantity - 1) {
          const valeursRestantes = valeurUniques.length - i; // -1 ?
          const groupesRestants = groupQuantity - i;
          console.log('valeursRestantes: %o', valeursRestantes);
          console.log('groupesRestants: %o', groupesRestants);

          if (valeursRestantes <= groupesRestants) {
            valeurClasseFinPosition = valeurUniques[i];
            console.log('valeurClasseFinPosition = %o', valeurClasseFinPosition);
          } else { // rééquilibrage des classes
            const indexValeurFinMax = valeurUniques.length - groupesRestants;
            const positionIdeale = quantiteValeursParClasse * (i + 1);
            const valeurFinTemporaire = valeurAttributs[positionIdeale];
            const indexValeurFinTemporaire = valeurUniques.indexOf(valeurFinTemporaire);

            if (indexValeurFinTemporaire > indexValeurFinMax) {
              valeurClasseFinPosition = valeurUniques[indexValeurFinMax];
              console.log('valeurClasseFinPosition1 = %o', valeurClasseFinPosition);
            } else {
              const nombreValeursAvant = valeurAttributs.indexOf(valeurFinTemporaire);
              const nombreValeursApres = valeurAttributs.indexOf(valeurUniques[indexValeurFinTemporaire + 1]) - 1;
              console.log('valeurFinTemporaire = %o; valeurUniques[indexValeurFinTemporaire - 1] = %o', valeurFinTemporaire, indexValeurFinTemporaire > 0 ? valeurUniques[indexValeurFinTemporaire - 1] : valeurUniques[0]);
              console.log('nombreValeursAvant = %o; nombreValeursApres = %o; positionIdeale = %o', nombreValeursAvant, nombreValeursApres, positionIdeale);

              if (positionIdeale - nombreValeursAvant === nombreValeursApres - positionIdeale) {
                if ((valeurAttributs.length % 2 === 0) || (valeurAttributs.length % 2 !== 0)) { //  && groupQuantity % 2 === 0
                  valeurClasseFinPosition = valeurFinTemporaire;
                } else {
                  valeurClasseFinPosition = indexValeurFinTemporaire > 0 ? valeurUniques[indexValeurFinTemporaire - 1] : valeurUniques[0];
                }
              } else if (positionIdeale - nombreValeursAvant < nombreValeursApres - positionIdeale) {
                valeurClasseFinPosition = valeurFinTemporaire;
              } else {
                valeurClasseFinPosition = indexValeurFinTemporaire > 0 ? valeurUniques[indexValeurFinTemporaire - 1] : valeurUniques[0];
              }
              console.log('valeurClasseFinPosition2 = %o', valeurClasseFinPosition); // ! encore bug avec pts districts 2 groupes
            }
          }
        } else {
          valeurClasseFinPosition = valeurUniques[valeurUniques.length - 1];
        }

        const valeurClasseFin = i > 0 && valeurClasseFinPosition <= valeurClasseDebut
          ? valeurUniques[indexValeurDebut + 1]
          : valeurClasseFinPosition;
        limites.push(valeurClasseFin);
      }
      groupsStat.bounds = limites;
      console.log('quantile - limites: %o', limites);
    }

    groupsStat = modifierClassificationSelonPrecision(groupsStat, precision);
    // ! NOTIFIER QUE PAS RÉEL QUANTILE
    return groupsStat;
  } */

  static obtenirStyleSelectionSelonType (typeVecteur, styleSimple) {
    switch (typeVecteur) {
      case FeatureType.POLYGON:
        return new Style({
          fill: new Fill({
            color: 'yellow'
          }),
          stroke: new Stroke({
            color: '#E9C603',
            width: styleSimple.strokeWidth
          }),
          zIndex: 3
        });
      case FeatureType.LINE:
        return new Style({
          stroke: new Stroke({
            color: 'yellow',
            width: styleSimple.strokeWidth
          }),
          zIndex: 3
        });
      case FeatureType.POINT:
        return new Style({
          image: new CircleStyle({
            radius: styleSimple.radius,
            fill: new Fill({
              color: 'yellow'
            })
          }),
          zIndex: 3
        });
      default:
        return new Style({
          fill: new Fill({
            color: 'yellow'
          }),
          stroke: new Stroke({
            color: 'yellow',
            width: styleSimple.strokeWidth
          }),
          zIndex: 3
        });
    }
  }

  /**
   * @param { object } style
   * @returns { Style }
   */
  static obtenirStylePolygone (style) {
    return new Style({
      fill: new Fill({
        color: style.fillColor
      }),
      stroke: new Stroke({
        color: style.strokeColor,
        width: style.strokeWidth
      }),
      zIndex: style.zIndex
    });
  }

  /**
   * @param { object } style
   * @returns { Style }
   */
  static obtenirStyleLigne (style) {
    return new Style({
      stroke: new Stroke({
        color: style.strokeColor,
        width: style.strokeWidth
      }),
      zIndex: style.zIndex
    });
  }

  /**
   * @param { object } style
   * @param { Feature } feature
   * @returns { Style }
   */
  static obtenirStylePoint (style, feature) {
    // const size = feature.get('features') ? feature.get('features').length : 0;
    // if (size > 1) {
    //   const olStyle = new Style({
    //     image: new CircleStyle({
    //       radius: 10,
    //       fill: new Fill({
    //         color: style.fillColor
    //       })
    //     }),
    //     text: new Text({
    //       text: size.toString(),
    //       fill: new Fill({
    //         color: '#000'
    //       }),
    //       offsetY: 1
    //     })
    //   });
    //   return olStyle;
    // } else {
    const olStyle = style.strokeWidth > 0
      ? new Style({
        image: new CircleStyle({
          radius: style.radius,
          fill: new Fill({
            color: style.fillColor
          }),
          stroke: new Stroke({
            color: style.strokeColor,
            width: style.strokeWidth
          }),
          zIndex: style.zIndex
        })
      })
      : new Style({
        image: new CircleStyle({
          radius: style.radius,
          fill: new Fill({
            color: style.fillColor
          })
        }),
        zIndex: style.zIndex
      });
    return olStyle;
    // }
  }

  static obtenirStyleParDéfaut (style) {
    return new Style({
      fill: new Fill({
        color: style.fillColor
      }),
      stroke: new Stroke({
        color: style.strokeColor,
        width: style.strokeWidth
      }),
      zIndex: 1
    });
  }

  static obtenirStyleLabel (label, styleLabel) { // , zIndex
    const _styleLabel = new Style({
      text: VectorLayerStyle.obtenirStyleText(label, styleLabel)
    }); // zIndex: zIndex
    return _styleLabel;
  }

  static obtenirStyleText (label, styleLabel) {
    const _styleText = new Text({
      text: diviserString(label, 20, '\n'),
      fill: new Fill({
        color: styleLabel.fontColor
      }),
      font: styleLabel.fontSize + 'px Arial,sans-serif',
      padding: styleLabel.padding,
      textAlign: 'center',
      stroke: new Stroke({
        width: styleLabel.haloWidth,
        color: styleLabel.haloColor
      }),
      backgroundFill: new Fill({
        color: styleLabel.backgroundColor
      }),
      offsetX: styleLabel.offsetX,
      offsetY: styleLabel.offsetY
    });
    return _styleText;
  }

  /*
   * JSON SPECIFIC
   */

  /** @returns { JSON } */
  toJSON () {
    return {
      styleType: this._styleType,
      symbolShape: this._symbolShape,
      groupValueColumn: this._groupValueColumn,
      symbolStyles: convertirSymbolStylesVersJSON(this._symbolStyles),
      singleStyle: this._singleStyle,
      colorGradient: this._colorGradient,
      colorTheme: this._colorTheme,
      classification: this._classification,
      gradationType: this._gradationType,
      styleAutres: this._styleAutres,
      styleLabel: this._styleLabel
    };
  }

  async creerClassificationDepuisJSON (valeursAttribut) {
    if (valeursAttribut[0] !== undefined || valeursAttribut[0] !== null) {
      const classificationJSON = this._classificationJSON;
      const precision = classificationJSON.precision;
      const nombreGroupes = classificationJSON.nbGroupes;
      let groupsStat;

      switch (classificationJSON.type) {
        case ClassificationType.ATTRIBUTE:
          groupsStat = VectorLayerStyle.obtenirClassificationParValeurAttributs(valeursAttribut);
          break;
        case ClassificationType.EQ_INTERVAL:
          groupsStat = VectorLayerStyle.obtenirClassificationParIntervalEgaux(valeursAttribut, precision, nombreGroupes);
          break;
        case ClassificationType.GEOMETRIC:
          groupsStat = VectorLayerStyle.obtenirClassificationParProgressionGeometrique(valeursAttribut, precision, nombreGroupes);
          break;
        case ClassificationType.JENKS:
          groupsStat = VectorLayerStyle.obtenirClassificationParJenks(valeursAttribut, precision, nombreGroupes);
          break;
        case ClassificationType.STD_DEVIATION:
          groupsStat = VectorLayerStyle.obtenirClassificationParEcartType(valeursAttribut);
          break;
        case ClassificationType.QUANTILE:
          console.log('classification quantile non-fonctionnelle');
          break;
        default:
          console.log('erreur classification');
      }

      await groupsStat.doCount();

      const classificationTemporaire = {
        type: classificationJSON.type,
        groups: await groupsStat,
        precision: precision,
        nbGroupes: nombreGroupes
      };

      const classification = await classificationTemporaire;

      this.classification = classification;
      return classification;
    } else {
      console.log(valeursAttribut[0]);
      this.classification = {};
      return {};
    }
  }
}

/**
 * @param { Map<number, GroupStyle> } symbolStyles
 * @returns { JSON }
 */
function convertirSymbolStylesVersJSON (symbolStyles) {
  const jsonSymbolStyles = [];
  for (const [key, value] of symbolStyles) {
    jsonSymbolStyles.push({ key: key, value: value });
  }
  return jsonSymbolStyles;
}

/**
 * @param { JSON } jsonSymbolStyles
 * @returns { Map }
 */
function convertirSymbolStylesDepuisJSON (jsonSymbolStyles) {
  const symbolStyles = new Map();
  jsonSymbolStyles.forEach(function (obj) {
    symbolStyles.set(obj.key, obj.value);
  });
  return symbolStyles;
}

/**
 * @param { Geostats } groupes
 * @param { number } precision
 * @returns { Geostats }
 */
function modifierClassificationSelonPrecision (groupes, precision) {
  const newBounds = [];
  const newRanges = [];
  const lang = window.localStorage.getItem('lang');
  const separator = lang === 'en' ? dictionary.classification.legendSeparator.en : dictionary.classification.legendSeparator.fr;

  groupes.setPrecision(precision);
  groupes.doCount();
  // console.log(groupes);
  try {
    // console.log(groupes.bounds);
    for (let i = 0; i < groupes.bounds.length; i++) {
      const bound = i === groupes.bounds.length - 1
        ? Math.ceil(groupes.bounds[i] * Math.pow(10, precision)) / Math.pow(10, precision)
        : Math.floor(groupes.bounds[i] * Math.pow(10, precision)) / Math.pow(10, precision);
      newBounds.push(bound);
      if (i > 0) {
        const startBound = i === 1
          ? newBounds[i - 1].toFixed(precision)
          : newBounds[i - 1] < newBounds[i]
            ? ((newBounds[i - 1] + (1 / Math.pow(10, precision))).toFixed(precision))
            : newBounds[i].toFixed(precision);

        const endBound = newBounds[i].toFixed(precision);

        // console.log('start: %o, end: %o', startBound, endBound);
        const range = startBound === endBound ? startBound : startBound + separator + newBounds[i].toFixed(precision);
        newRanges.push(range);
      }
    }
    groupes.bounds = newBounds;
    groupes.ranges = newRanges;
  } catch (err) {
    console.log(err);
    groupes.setRanges();
  }

  return groupes;
}

/**
 * @param { string } str
 * @param { number } width
 * @param { string } spaceReplacer
 * @returns { string }
 */
function diviserString (str, width, spaceReplacer) {
  if (str.length > width) {
    let p = width;
    while (p > 0 && (str[p] !== ' ' && str[p] !== '-')) {
      p--;
    }
    if (p > 0) {
      let left;
      if (str.substring(p, p + 1) === '-') {
        left = str.substring(0, p + 1);
      } else {
        left = str.substring(0, p);
      }
      const right = str.substring(p + 1);
      return left + spaceReplacer + diviserString(right, width, spaceReplacer);
    }
  }
  return str;
}
